<template>
    <!-- *********************************************
    *               Non-Authenticated Area           *
    ********************************************** -->
    <!-- Non-authenticated Area -->
    <router-view
    v-if="nonAuthenticatedArea"
    @backOrCloseClicked="navigateByName(routeList.LOGIN_NAME)"
    @showFeedbackToast="[showFeedbackToast = true, feedbackToastData = $event]"
    @updateLoginLoader="emit('updateLoginLoader', $event)"/>
    <!-- *********************************************
    *                 Authenticated Area             *
    ********************************************** -->
    <div v-else class="authenticated-area" :class="{'menu-collapsed': menuCollapsed}">
        <!-- Lateral Menu -->
        <navigation-web-menu-base
        :menuCollapsed="menuCollapsed"
        :applyLoginLoaderExitAnimation="showLoginSpinner"
        @expandMenu="menuCollapsed = false"/>
        <!-- PopUp Modals Wrapper-->
        <pop-ups-wrapper v-if="showPopupsList && !showLoginSpinner"/>
        <!-- Right-side Wrapper -->
        <div class="right-side-wrapper" :class="{'lighter-background': applyLighterBackground}">
            <!-- Primary Button -->
            <button-primary
            buttonStyle="round"
            buttonSize="small"
            :icon="menuCollapsed ? 'arrow-right-circle' : 'arrow-left-circle'"
            @clicked="menuCollapsed = !menuCollapsed"/>
            <!-- Menu -->
            <navigation-header-menu/>
            <!-- Views -->
            <router-view
            :key="$route.fullPath"
            :menuCollapsed="menuCollapsed"
            :applyHomeRenderDelay="showLoginSpinner"
            @updateLoginLoader="emit('updateLoginLoader', $event)"
            @applyLoginLoaderExitAnimation="emit('applyLoginLoaderExitAnimation')"/>
        </div>
        <!-- Session Expired Modal -->
		<expired-session-modal v-if="getSessionExpiredState"/>
    </div>
    <!-- Feedback Toasts -->
    <toasts-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import NavigationWebMenuBase from '@/components/generic/navigation/web/MenuBase'
import ButtonPrimary from '@/components/generic/buttons/Primary'
import NavigationHeaderMenu from '@/components/generic/navigation/header/Menu'
import ExpiredSessionModal from '@/components/generic/modals/centered/ExpiredSession'
import ToastsGeneric from '@/components/generic/toasts/Generic'
import PopUpsWrapper from '@/components/generic/modals/centered/popUps/PopUpsWrapper'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
/* Vue */
import { ref } from 'vue'
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'

/* Emmitters */
const emit = defineEmits(['updateLoginLoader', 'applyLoginLoaderExitAnimation'])

/* Props */
const props = defineProps({
	nonAuthenticatedArea: Boolean,
    applyLighterBackground: Boolean,
    showLoginSpinner: Boolean
})

const { getSessionExpiredState, showPopupsList } = mapGetters(moduleMapper.SESSION)

const menuCollapsed = ref(false)
const showFeedbackToast = ref(false)
const feedbackToastData = ref(null)
</script>

<style lang="scss" scoped>
.authenticated-area {
    width: 100%;
    height: 100%;

    display: flex;
    overflow: hidden;

    &:before {
        content: '';
        width: calc(284px + 16px); /* Menu max-width + radios-of right-side-wrapper */
        height: 100%;

        position: absolute;
        background-image: var(--gradient);
        transition: width .5s ease-in-out;
    }

    &.menu-collapsed {
        &:before {
            width: calc(80px + 16px); /* Menu max-width + radios-of right-side-wrapper */
        }
    }

    .right-side-wrapper {
        width: 100%;
        height: 100%;

        display: flex;
        flex-direction: column;
        position: relative;
        overflow: hidden;

        background-color: var(--secondary-light-3);
        border-top-left-radius: 16px;

        &.lighter-background {
            background-color: var(--secondary-light-4);
        }

        .button-primary {
            position: fixed;
            top: 22px;
            transform: translateX(-50%);

            background-color: var(--menu-collapse-button-bg);
            z-index: 1;

            :deep(svg) {
                .change-color-fill {
                    fill: var(--menu-collapse-button-icon);
                }
            }
        }

        .header-placeholder {
            width: 100%;
            height: 64px;

            padding-left: 24px;
            line-height: 64px;

            flex-shrink: 0;
        }
    }
}
</style>
